import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress, Alert,
  IconButton, Tooltip, TextField, Switch, FormControlLabel,
  Button, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Tabs, Tab, MenuItem,
} from '@mui/material';
import {
  ArrowBack, Edit, Save, Cancel as CancelIcon, CheckCircle, Cancel,
  Download, Delete, InsertDriveFile, PictureAsPdf, Description,
  Gavel, Notes, Assignment, NoteAdd, UploadFile,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../services/api';

const API = API_BASE_URL;

interface Minuta {
  id: string;
  expediente_id: string | null;
  juzgado: string | null;
  ciudad: string | null;
  num_procedimiento: string | null;
  importe_minuta: number | null;
  fecha_presentacion_minuta: string | null;
  requerimiento_solicitado: boolean;
  fecha_requerimiento: string | null;
  monitorio_iniciado: boolean;
  cobrado: boolean;
  fecha_cobro: string | null;
  cobro_gerencia_solicitado: boolean;
  fecha_solicitud_cobro_gerencia: string | null;
  notas: string | null;
  numero_orden: number | null;
  juzgado_formal: string | null;
  procedimiento_documento: string | null;
  proviene_de_texto: string | null;
  nig_documento: string | null;
  estado_respuesta: string;
  respuesta_documento_id: string | null;
  respuesta_revisada_at: string | null;
  respuesta_revisada_por: string | null;
  created_at: string;
  updated_at: string;
}

interface Documento {
  id: string;
  tipo_documento: string;
  numero_documento: string | null;
  nombre_archivo: string | null;
  mime_type: string | null;
  tamaño_kb: number | null;
  estado: string | null;
  version: number | null;
  generated_automatically?: boolean;
  generado_automaticamente: boolean;
  es_documentacion_judicial?: boolean;
  origen_tipo?: string | null;
  origen_ruta_relativa?: string | null;
  expediente_origen_id?: string | null;
  expediente_origen_codigo?: string | null;
  expediente_origen_codigo_actual?: string | null;
  expediente_origen_anio?: number | null;
  expediente_origen_numero?: number | null;
  expediente_origen_tipo?: string | null;
  fecha_generacion: string | null;
  tiempo_generacion_segundos: number | null;
  created_at: string;
}

function fmt(date: string | null) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('es-ES');
}

function fmtEur(n: number | null) {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

function estadoChip(m: Minuta) {
  if (m.cobrado) return <Chip label="Cobrado" color="success" size="small" icon={<CheckCircle />} />;
  if (m.monitorio_iniciado) return <Chip label="Monitorio activo" color="warning" size="small" />;
  if (m.requerimiento_solicitado) return <Chip label="Requerimiento solicitado" color="info" size="small" />;
  return <Chip label="Pendiente" color="default" size="small" />;
}

function respuestaChip(m: Minuta) {
  const estado = m.estado_respuesta || 'No consta';
  const color = estado === 'Respuesta recibida' || estado === 'Resuelta'
    ? 'success'
    : estado === 'Pendiente de revisión' ? 'warning' : 'default';
  return <Chip label={`Respuesta: ${estado}`} color={color} size="small" />;
}

function fileIcon(tipo: string) {
  if (tipo === 'pdf') return <PictureAsPdf color="error" />;
  if (tipo === 'docx' || tipo === 'doc') return <Description color="primary" />;
  return <InsertDriveFile color="action" />;
}

// ─── Sección editable de datos básicos (solo admin) ──────────────────────────
interface BasicosState {
  juzgado: string;
  ciudad: string;
  num_procedimiento: string;
  importe_minuta: string;
  fecha_presentacion_minuta: string;
}

// ─── Sección editable de gestión ─────────────────────────────────────────────
interface GestionState {
  requerimiento_solicitado: boolean;
  fecha_requerimiento: string;
  monitorio_iniciado: boolean;
  cobrado: boolean;
  fecha_cobro: string;
  cobro_gerencia_solicitado: boolean;
  fecha_solicitud_cobro_gerencia: string;
  notas: string;
  estado_respuesta: string;
  respuesta_documento_id: string;
}

export default function MinutaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isAbogado } = useAuth();

  const [minuta, setMinuta] = useState<Minuta | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [documentTab, setDocumentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edición datos básicos (admin)
  const [editingBasicos, setEditingBasicos] = useState(false);
  const [basicosState, setBasicosState] = useState<BasicosState>({
    juzgado: '', ciudad: '', num_procedimiento: '', importe_minuta: '', fecha_presentacion_minuta: '',
  });

  // Edición gestión
  const [editingGestion, setEditingGestion] = useState(false);
  const [gestionState, setGestionState] = useState<GestionState>({
    requerimiento_solicitado: false, fecha_requerimiento: '',
    monitorio_iniciado: false, cobrado: false, fecha_cobro: '',
    cobro_gerencia_solicitado: false, fecha_solicitud_cobro_gerencia: '', notas: '',
    estado_respuesta: 'No consta', respuesta_documento_id: '',
  });

  const [saving, setSaving] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);

  // Dialog generación escrito
  const [escritoDialog, setEscritoDialog] = useState(false);
  const [escritoParams, setEscritoParams] = useState({
    juzgado_formal: '',
    provincia: 'Badajoz',
    ciudad_firma: 'Cáceres',
    fecha_firma: '',
  });
  const [generando, setGenerando] = useState(false);

  // Dialog subida de documentación
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [uploadError, setUploadError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const fetchMinuta = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/minutas/${id}`);
      const m: Minuta = res.data;
      setMinuta(m);
      setBasicosState({
        juzgado: m.juzgado ?? '',
        ciudad: m.ciudad ?? '',
        num_procedimiento: m.num_procedimiento ?? '',
        importe_minuta: m.importe_minuta?.toString() ?? '',
        fecha_presentacion_minuta: m.fecha_presentacion_minuta ?? '',
      });
      setGestionState({
        requerimiento_solicitado: m.requerimiento_solicitado,
        fecha_requerimiento: m.fecha_requerimiento ?? '',
        monitorio_iniciado: m.monitorio_iniciado,
        cobrado: m.cobrado,
        fecha_cobro: m.fecha_cobro ?? '',
        cobro_gerencia_solicitado: m.cobro_gerencia_solicitado,
        fecha_solicitud_cobro_gerencia: m.fecha_solicitud_cobro_gerencia ?? '',
        notas: m.notas ?? '',
        estado_respuesta: m.estado_respuesta ?? 'No consta',
        respuesta_documento_id: m.respuesta_documento_id ?? '',
      });

      // Cargar documentos si tiene expediente asociado
      if (m.expediente_id) {
        const docsRes = await axios.get(`${API}/api/minutas/${m.id}/documentos`);
        setDocumentos(docsRes.data);
      } else {
        setDocumentos([]);
      }
    } catch {
      setError('Error al cargar la minuta');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchMinuta(); }, [fetchMinuta]);

  const saveBasicos = async () => {
    if (!minuta) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/minutas/${minuta.id}`, {
        juzgado: basicosState.juzgado || null,
        ciudad: basicosState.ciudad || null,
        num_procedimiento: basicosState.num_procedimiento || null,
        importe_minuta: basicosState.importe_minuta ? parseFloat(basicosState.importe_minuta) : null,
        fecha_presentacion_minuta: basicosState.fecha_presentacion_minuta || null,
      });
      setEditingBasicos(false);
      await fetchMinuta();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveGestion = async () => {
    if (!minuta) return;
    setSaving(true);
    try {
      const body: any = {
        requerimiento_solicitado: gestionState.requerimiento_solicitado,
        monitorio_iniciado: gestionState.monitorio_iniciado,
        cobrado: gestionState.cobrado,
        cobro_gerencia_solicitado: gestionState.cobro_gerencia_solicitado,
        fecha_solicitud_cobro_gerencia: gestionState.cobro_gerencia_solicitado
          ? (gestionState.fecha_solicitud_cobro_gerencia || null)
          : null,
        notas: gestionState.notas || null,
        fecha_requerimiento: gestionState.requerimiento_solicitado
          ? (gestionState.fecha_requerimiento || null)
          : null,
        fecha_cobro: gestionState.cobrado
          ? (gestionState.fecha_cobro || null)
          : null,
        estado_respuesta: gestionState.estado_respuesta,
        respuesta_documento_id: gestionState.respuesta_documento_id || null,
      };
      await axios.put(`${API}/api/minutas/${minuta.id}`, body);
      setEditingGestion(false);
      await fetchMinuta();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const openEscritoDialog = () => {
    if (!minuta) return;
    setEscritoParams({
      juzgado_formal: minuta.juzgado_formal ?? '',
      provincia: 'Badajoz',
      ciudad_firma: 'Cáceres',
      fecha_firma: new Date().toISOString().split('T')[0],
    });
    setEscritoDialog(true);
  };

  const generarEscrito = async () => {
    if (!minuta) return;
    setGenerando(true);
    try {
      await axios.post(`${API}/api/minutas/${minuta.id}/generar-escrito`, {
        juzgado_formal: escritoParams.juzgado_formal || null,
        provincia: escritoParams.provincia,
        ciudad_firma: escritoParams.ciudad_firma,
        fecha_firma: escritoParams.fecha_firma || null,
      });
      setEscritoDialog(false);
      await fetchMinuta();  // recargar documentos
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al generar el escrito');
    } finally {
      setGenerando(false);
    }
  };

  const openUploadDialog = () => {
    setUploadFiles([]);
    setUploadError('');
    setUploadProgress({ done: 0, total: 0 });
    setUploadDialog(true);
  };

  const addUploadFiles = (files: File[]) => {
    if (!files.length) return;
    setUploadFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of files) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      }
      return merged;
    });
  };

  // Recorre entradas soltadas (ficheros y carpetas) y devuelve todos los ficheros, recursivamente.
  const filesFromDataTransferItems = async (items: DataTransferItemList): Promise<File[]> => {
    const readEntry = (entry: any): Promise<File[]> => {
      return new Promise(resolve => {
        if (entry.isFile) {
          entry.file((file: File) => resolve([file]), () => resolve([]));
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const allEntries: any[] = [];
          const readBatch = () => {
            reader.readEntries(async (batch: any[]) => {
              if (!batch.length) {
                const nested = await Promise.all(allEntries.map(readEntry));
                resolve(nested.flat());
                return;
              }
              allEntries.push(...batch);
              readBatch();
            }, () => resolve([]));
          };
          readBatch();
        } else {
          resolve([]);
        }
      });
    };

    const entries = Array.from(items)
      .map(item => (item as any).webkitGetAsEntry?.())
      .filter(Boolean);
    const results = await Promise.all(entries.map(readEntry));
    return results.flat();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.items && e.dataTransfer.items.length) {
      const files = await filesFromDataTransferItems(e.dataTransfer.items);
      addUploadFiles(files);
    } else if (e.dataTransfer.files) {
      addUploadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeUploadFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const subirDocumento = async () => {
    if (!minuta || !uploadFiles.length) return;
    setUploading(true);
    setUploadError('');
    setUploadProgress({ done: 0, total: uploadFiles.length });
    const errores: string[] = [];
    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      try {
        const formData = new FormData();
        formData.append('archivo', file);
        await axios.post(`${API}/api/minutas/${minuta.id}/documentos/subir`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (e: any) {
        const detail = e?.response?.data?.detail;
        errores.push(`${file.name}: ${typeof detail === 'string' ? detail : 'error al subir'}`);
      }
      setUploadProgress({ done: i + 1, total: uploadFiles.length });
    }
    await fetchMinuta();
    setUploading(false);
    if (errores.length) {
      setUploadError(errores.join(' · '));
      setUploadFiles(prev => prev.filter(f => errores.some(err => err.startsWith(f.name))));
    } else {
      setUploadDialog(false);
      setUploadFiles([]);
    }
  };

  const handleDownload = async (docId: string, nombre: string) => {
    try {
      const res = await axios.get(`${API}/api/documentos/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre || docId;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Error al descargar el documento');
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    setDeletingDoc(docId);
    try {
      await axios.delete(`${API}/api/documentos/${docId}`);
      setDocumentos(prev => prev.filter(d => d.id !== docId));
    } catch {
      setError('Error al eliminar el documento');
    } finally {
      setDeletingDoc(null);
    }
  };

  const documentosJudiciales = documentos.filter(doc => doc.es_documentacion_judicial);
  const documentosGenerados = documentos.filter(doc => !doc.es_documentacion_judicial);
  const documentosVisibles = documentTab === 0 ? documentosGenerados : documentosJudiciales;

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (!minuta) return <Alert severity="error">Minuta no encontrada</Alert>;

  return (
    <Box>
      {/* ── Cabecera ── */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Tooltip title="Volver al listado">
          <IconButton onClick={() => navigate('/minutas')}><ArrowBack /></IconButton>
        </Tooltip>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h5" fontWeight={700}>
              {minuta.num_procedimiento ?? 'Minuta sin procedimiento'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {estadoChip(minuta)}
              {respuestaChip(minuta)}
            </Stack>
            {minuta.numero_orden && (
              <Chip label={`# ${minuta.numero_orden}`} size="small" variant="outlined" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {minuta.juzgado} · {minuta.ciudad}
          </Typography>
        </Box>
        {isAdmin && minuta.expediente_id && (
          <Button variant="outlined" size="small" startIcon={<Edit />}
            onClick={() => navigate(`/expedientes/${minuta.expediente_id}`)}>
            Editar expediente
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* ── Datos básicos ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Assignment color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>Datos del procedimiento</Typography>
              </Box>
              {isAdmin && !editingBasicos && (
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => setEditingBasicos(true)}><Edit fontSize="small" /></IconButton>
                </Tooltip>
              )}
              {editingBasicos && (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Guardar">
                    <span>
                      <IconButton size="small" color="success" onClick={saveBasicos} disabled={saving}>
                        {saving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancelar">
                    <IconButton size="small" onClick={() => setEditingBasicos(false)}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {editingBasicos ? (
                <>
                  <TextField label="Juzgado" size="small" fullWidth value={basicosState.juzgado}
                    onChange={e => setBasicosState(s => ({ ...s, juzgado: e.target.value }))} />
                  <TextField label="Ciudad" size="small" fullWidth value={basicosState.ciudad}
                    onChange={e => setBasicosState(s => ({ ...s, ciudad: e.target.value }))} />
                  <TextField label="Nº Procedimiento" size="small" fullWidth value={basicosState.num_procedimiento}
                    onChange={e => setBasicosState(s => ({ ...s, num_procedimiento: e.target.value }))} />
                  <TextField label="Importe (€)" size="small" type="number" fullWidth value={basicosState.importe_minuta}
                    onChange={e => setBasicosState(s => ({ ...s, importe_minuta: e.target.value }))} />
                  <TextField label="Fecha presentación minuta" size="small" type="date" fullWidth
                    value={basicosState.fecha_presentacion_minuta}
                    onChange={e => setBasicosState(s => ({ ...s, fecha_presentacion_minuta: e.target.value }))}
                    InputLabelProps={{ shrink: true }} />
                </>
              ) : (
                <>
                  <DataRow label="Juzgado" value={minuta.juzgado} />
                  <DataRow label="Ciudad" value={minuta.ciudad} />
                  <DataRow label="Nº Procedimiento" value={minuta.num_procedimiento} />
                  <DataRow label="Importe minuta" value={fmtEur(minuta.importe_minuta)} highlight />
                  <DataRow label="Fecha presentación" value={fmt(minuta.fecha_presentacion_minuta)} />
                </>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* ── Gestión de cobro ── */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Gavel color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>Gestión de cobro</Typography>
              </Box>
              {!editingGestion && (
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => setEditingGestion(true)}><Edit fontSize="small" /></IconButton>
                </Tooltip>
              )}
              {editingGestion && (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Guardar">
                    <span>
                      <IconButton size="small" color="success" onClick={saveGestion} disabled={saving}>
                        {saving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancelar">
                    <IconButton size="small" onClick={() => setEditingGestion(false)}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {editingGestion ? (
                <>
                  <FormControlLabel
                    control={<Switch checked={gestionState.requerimiento_solicitado}
                      onChange={e => setGestionState(s => ({ ...s, requerimiento_solicitado: e.target.checked }))} />}
                    label="Requerimiento judicial solicitado"
                  />
                  {gestionState.requerimiento_solicitado && (
                    <TextField label="Fecha requerimiento" size="small" type="date" fullWidth
                      value={gestionState.fecha_requerimiento}
                      onChange={e => setGestionState(s => ({ ...s, fecha_requerimiento: e.target.value }))}
                      InputLabelProps={{ shrink: true }} />
                  )}
                  <FormControlLabel
                    control={<Switch checked={gestionState.monitorio_iniciado}
                      onChange={e => setGestionState(s => ({ ...s, monitorio_iniciado: e.target.checked }))} />}
                    label="Monitorio iniciado"
                  />
                  <FormControlLabel
                    control={<Switch checked={gestionState.cobrado} color="success"
                      onChange={e => setGestionState(s => ({ ...s, cobrado: e.target.checked }))} />}
                    label="Cobrado"
                  />
                  {gestionState.cobrado && (
                    <TextField label="Fecha de cobro" size="small" type="date" fullWidth
                      value={gestionState.fecha_cobro}
                      onChange={e => setGestionState(s => ({ ...s, fecha_cobro: e.target.value }))}
                      InputLabelProps={{ shrink: true }} />
                  )}
                  <FormControlLabel
                    control={<Switch checked={gestionState.cobro_gerencia_solicitado} color="info"
                      onChange={e => setGestionState(s => ({ ...s, cobro_gerencia_solicitado: e.target.checked }))} />}
                    label="Cobro solicitado a Gerencia"
                  />
                  {gestionState.cobro_gerencia_solicitado && (
                    <TextField label="Fecha solicitud a Gerencia" size="small" type="date" fullWidth
                      value={gestionState.fecha_solicitud_cobro_gerencia}
                      onChange={e => setGestionState(s => ({ ...s, fecha_solicitud_cobro_gerencia: e.target.value }))}
                      InputLabelProps={{ shrink: true }} />
                  )}
                  <TextField select label="Estado de respuesta" size="small" fullWidth
                    value={gestionState.estado_respuesta}
                    onChange={e => setGestionState(s => ({ ...s, estado_respuesta: e.target.value }))}>
                    {['No consta', 'Pendiente de revisión', 'Respuesta recibida', 'Sin respuesta acreditada', 'Impugnada', 'Resuelta'].map(estado => (
                      <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select label="Documento acreditativo" size="small" fullWidth
                    value={gestionState.respuesta_documento_id}
                    onChange={e => setGestionState(s => ({ ...s, respuesta_documento_id: e.target.value }))}
                    helperText="Obligatorio si la respuesta es recibida o resuelta">
                    <MenuItem value="">Ninguno</MenuItem>
                    {documentosJudiciales.map(doc => (
                      <MenuItem key={doc.id} value={doc.id}>{doc.nombre_archivo || doc.id}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Observaciones" size="small" fullWidth multiline rows={5}
                    value={gestionState.notas}
                    onChange={e => setGestionState(s => ({ ...s, notas: e.target.value }))}
                    placeholder="Escriba aquí cualquier observación de la minuta o del expediente..." />
                </>
              ) : (
                <>
                  <DataRow label="Requerimiento solicitado"
                    value={minuta.requerimiento_solicitado
                      ? <CheckCircle sx={{ color: 'info.main', fontSize: 20, verticalAlign: 'middle' }} />
                      : <Cancel sx={{ color: 'grey.400', fontSize: 20, verticalAlign: 'middle' }} />} />
                  {minuta.requerimiento_solicitado && (
                    <DataRow label="Fecha requerimiento" value={fmt(minuta.fecha_requerimiento)} />
                  )}
                  <DataRow label="Monitorio iniciado"
                    value={minuta.monitorio_iniciado
                      ? <CheckCircle sx={{ color: 'warning.main', fontSize: 20, verticalAlign: 'middle' }} />
                      : <Cancel sx={{ color: 'grey.400', fontSize: 20, verticalAlign: 'middle' }} />} />
                  {minuta.cobro_gerencia_solicitado && (
                    <DataRow label="Solicitud a Gerencia" value={fmt(minuta.fecha_solicitud_cobro_gerencia)} />
                  )}
                  <DataRow label="Estado" value={estadoChip(minuta)} />
                  {minuta.cobrado && (
                    <DataRow label="Fecha de cobro" value={fmt(minuta.fecha_cobro)} />
                  )}
                </>
              )}
            </Stack>
          </Paper>
        </Grid>

      {/* ── Observaciones de la minuta ── */}
      <Grid size={12} sx={{ mt: 3 }}>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <Notes color="action" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Observaciones</Typography>
            </Box>
            {!editingGestion && (
              <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => setEditingGestion(true)}>
                Editar observaciones
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} color={minuta.notas ? 'text.primary' : 'text.secondary'}>
            {minuta.notas || 'Sin observaciones registradas.'}
          </Typography>
        </Paper>
      </Grid>

      {/* ── Documentos ── */}
        <Grid size={12}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <InsertDriveFile color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>Documentos</Typography>
                <Chip label={documentos.length} size="small" variant="outlined" />
              </Box>
              <Stack direction="row" spacing={1}>
                {(isAdmin || isAbogado) && documentTab === 1 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<UploadFile />}
                    onClick={openUploadDialog}
                    disabled={!minuta?.expediente_id}
                  >
                    Subir documentación
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<NoteAdd />}
                  onClick={openEscritoDialog}
                  disabled={!minuta?.expediente_id}
                >
                  Generar escrito
                </Button>
              </Stack>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Tabs value={documentTab} onChange={(_, value) => setDocumentTab(value)} sx={{ mb: 2 }}>
              <Tab label={`Generados (${documentosGenerados.length})`} />
              <Tab label={`Judiciales / contestaciones (${documentosJudiciales.length})`} />
            </Tabs>

            {!minuta.expediente_id ? (
              <Typography color="text.secondary" variant="body2">
                Esta minuta no tiene expediente asociado. Los documentos se gestionan desde el expediente.
              </Typography>
            ) : documentosVisibles.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No hay documentos en esta categoría todavía.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Expediente de origen</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tamaño</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentosVisibles.map(doc => (
                      <TableRow key={doc.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {fileIcon(doc.tipo_documento)}
                            <Chip label={doc.tipo_documento.toUpperCase()} size="small" color="primary" />
                            {doc.generado_automaticamente && (
                              <Chip label="AUTO" size="small" color="success" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {doc.nombre_archivo || `${doc.tipo_documento}_${doc.id.slice(0, 8)}`}
                          </Typography>
                          {doc.numero_documento && (
                            <Typography variant="caption" color="text.secondary">Nº: {doc.numero_documento}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {doc.expediente_origen_tipo && doc.expediente_origen_numero && doc.expediente_origen_anio
                              ? `${doc.expediente_origen_tipo}-${doc.expediente_origen_numero}-${String(doc.expediente_origen_anio).slice(-2)}`
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={doc.estado || 'generado'}
                            color={doc.estado === 'generado' ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{doc.tamaño_kb ? `${doc.tamaño_kb} KB` : '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {fmt(doc.fecha_generacion ?? doc.created_at)}
                          </Typography>
                          {doc.tiempo_generacion_segundos && (
                            <Typography variant="caption" color="text.secondary">
                              {doc.tiempo_generacion_segundos}s
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Descargar">
                              <IconButton size="small" color="primary"
                                onClick={() => handleDownload(doc.id, doc.nombre_archivo ?? doc.id)}>
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(isAdmin || doc.tipo_documento === 'escrito_presentacion') && (
                              <Tooltip title="Eliminar">
                                <span>
                                  <IconButton size="small" color="error"
                                    onClick={() => handleDeleteDoc(doc.id)}
                                    disabled={deletingDoc === doc.id}>
                                    {deletingDoc === doc.id
                                      ? <CircularProgress size={14} />
                                      : <Delete fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Dialog: Generar Escrito de Presentación ── */}
      <Dialog open={escritoDialog} onClose={() => setEscritoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <NoteAdd color="primary" />
            <Typography fontWeight={700}>Generar Escrito de Presentación</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Revisa y ajusta los datos antes de generar el documento.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Cabecera del juzgado"
              fullWidth size="small"
              value={escritoParams.juzgado_formal}
              onChange={e => setEscritoParams(p => ({ ...p, juzgado_formal: e.target.value }))}
              helperText='Ej: "AL JUZGADO DE INSTRUCCIÓN Nº 3 DE ALMENDRALEJO (BADAJOZ)"'
              multiline rows={2}
            />
            <TextField
              label="Provincia"
              size="small" fullWidth
              value={escritoParams.provincia}
              onChange={e => setEscritoParams(p => ({ ...p, provincia: e.target.value }))}
            />
            <TextField
              label="Ciudad de firma"
              size="small" fullWidth
              value={escritoParams.ciudad_firma}
              onChange={e => setEscritoParams(p => ({ ...p, ciudad_firma: e.target.value }))}
            />
            <TextField
              label="Fecha de firma"
              size="small" fullWidth type="date"
              value={escritoParams.fecha_firma}
              onChange={e => setEscritoParams(p => ({ ...p, fecha_firma: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Procedimiento en el documento</Typography>
              <Typography variant="body2" fontWeight={600}>
                {minuta?.procedimiento_documento ?? minuta?.num_procedimiento ?? '—'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEscritoDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={generarEscrito}
            disabled={generando}
            startIcon={generando ? <CircularProgress size={16} /> : <NoteAdd />}
          >
            {generando ? 'Generando…' : 'Generar documento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Subir documentación judicial ── */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadFile color="primary" />
            <Typography fontWeight={700}>Subir documentación judicial</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Los documentos quedarán vinculados automáticamente al expediente de esta minuta
            ({minuta.num_procedimiento ?? minuta.id}). Revísalos antes de confirmar.
          </Typography>
          {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>{uploadError}</Alert>}

          <Box
            onDragOver={e => { e.preventDefault(); if (!uploading) setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={uploading ? undefined : handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              bgcolor: dragActive ? 'action.hover' : 'transparent',
              transition: 'background-color 0.15s, border-color 0.15s',
            }}
          >
            <UploadFile color={dragActive ? 'primary' : 'disabled'} sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Arrastra aquí ficheros o una carpeta completa
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<UploadFile />}
                disabled={uploading}
              >
                Seleccionar ficheros
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={e => { addUploadFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
                />
              </Button>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<UploadFile />}
                disabled={uploading}
              >
                Seleccionar carpeta
                <input
                  type="file"
                  hidden
                  // @ts-ignore -- atributos no estándar soportados por navegadores basados en Chromium/WebKit
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={e => { addUploadFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
                />
              </Button>
            </Stack>
          </Box>

          {uploadFiles.length > 0 && (
            <Box sx={{ mt: 2, maxHeight: 240, overflowY: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                {uploadFiles.length} fichero{uploadFiles.length !== 1 ? 's' : ''} seleccionado{uploadFiles.length !== 1 ? 's' : ''}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {uploadFiles.map((f, i) => (
                  <Box
                    key={`${f.name}-${f.size}-${i}`}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{f.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(f.size / 1024).toFixed(0)} KB
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeUploadFile(i)} disabled={uploading}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {uploading && uploadProgress.total > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Subiendo {uploadProgress.done} de {uploadProgress.total}…
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={subirDocumento}
            disabled={uploading || !uploadFiles.length}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFile />}
          >
            {uploading
              ? 'Subiendo…'
              : `Confirmar subida${uploadFiles.length ? ` (${uploadFiles.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Componente auxiliar para filas de datos ───────────────────────────────────
function DataRow({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={highlight ? 700 : 400}
        color={highlight ? 'primary.main' : 'text.primary'}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}
